import { z } from 'zod';
import { LANGUAGE_CODES, TRANSLATABLE_LANGUAGE_CODES } from '~shared/i18n/languages';
import { hasMarkdownLink } from '~shared/markdown-link';

// Любой API-эндпоинт: опиши тут zod-схему ответа, выведи тип через z.infer,
// и используй ОДНУ и ТУ ЖЕ схему на сервере (валидация ответа) и на клиенте
// (валидация полученного JSON). Так клиент и сервер не разойдутся в контракте.

// --- Tickets (акция «Ничего лишнее!») ---

/** MANUAL — ручное начисление сотрудником (simplify-ticket-rules). */
export const ConditionTypeSchema = z.enum(['PURCHASE', 'RECRUIT', 'RETENTION', 'MANUAL']);

export const TicketSchema = z.object({
  id: z.number().int().positive(),
  participantId: z.string(),
  originParticipantId: z.string(),
  participantType: z.enum(['MANAGER', 'CLIENT']),
  conditionType: ConditionTypeSchema,
  sourceRef: z.string(),
  period: z.string(),
  status: z.enum(['NEW', 'OPENED', 'REVOKED', 'PENDING_REVIEW']),
  prizeId: z.number().int().positive().nullable(),
  revokedReason: z.string().nullable(),
  /** Билет исключён из пересчёта: «Вернуть», отмена приза, ручные начисление и списание. */
  syncLockedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TicketDto = z.infer<typeof TicketSchema>;

export const TicketBalanceSchema = z.object({
  participantId: z.string(),
  activeCount: z.number().int().nonnegative(),
});
export type TicketBalance = z.infer<typeof TicketBalanceSchema>;

export const TicketHistoryResponseSchema = z.array(TicketSchema);

export const TicketHistoryQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(100),
  offset: z.coerce.number().int().min(0).default(0),
  order: z.enum(['asc', 'desc']).default('asc'),
});
export type TicketHistoryQuery = z.infer<typeof TicketHistoryQuerySchema>;

/** Ручное начисление или списание козочек участника (ticket-accrual, «Ручное начисление/списание козочек»). */
export const ManualTicketsInputSchema = z.object({
  count: z.number().int().min(1).max(1000),
  reason: z.string().trim().min(1).max(1000),
});
export type ManualTicketsInputDto = z.infer<typeof ManualTicketsInputSchema>;

// --- Поиск билетов и журнал событий (ticket-admin-tools) ---

/** `PARTICIPANT` — текущий или исходный владелец: вся история козочек участника, включая перенесённые (add-participant-admin). */
export const TicketSearchBySchema = z.enum(['OWNER', 'ORIGIN', 'ORDER', 'PARTNER', 'PARTICIPANT']);
export type TicketSearchBy = z.infer<typeof TicketSearchBySchema>;

const pageQuery = {
  limit: z.coerce.number().int().min(1).max(99).default(50),
  offset: z.coerce.number().int().min(0).default(0),
};

/** by+value — поиск по критерию; ни того, ни другого — последние начисленные билеты. */
export const TicketSearchQuerySchema = z
  .object({ by: TicketSearchBySchema.optional(), value: z.string().trim().min(1).max(64).optional(), ...pageQuery })
  .refine((q) => (q.by === undefined) === (q.value === undefined), { message: 'by и value задаются вместе', path: ['value'] })
  .refine((q) => q.by !== 'ORDER' || /^\d+$/.test(q.value ?? ''), { message: 'Номер заказа — только цифры', path: ['value'] });
export type TicketSearchQuery = z.infer<typeof TicketSearchQuerySchema>;

export const TicketSearchResponseSchema = z.object({
  items: z.array(TicketSchema),
  hasMore: z.boolean(),
});
export type TicketSearchResponse = z.infer<typeof TicketSearchResponseSchema>;

/** Журнал: ticketId — события билета; by+value — билетов по критерию; ничего — общая лента. */
export const TicketEventsQuerySchema = z
  .object({
    ticketId: z.coerce.number().int().positive().optional(),
    by: TicketSearchBySchema.optional(),
    value: z.string().trim().min(1).max(64).optional(),
    ...pageQuery,
  })
  .refine((q) => (q.by === undefined) === (q.value === undefined), { message: 'by и value задаются вместе', path: ['value'] })
  .refine((q) => q.by !== 'ORDER' || /^\d+$/.test(q.value ?? ''), { message: 'Номер заказа — только цифры', path: ['value'] });
export type TicketEventsQuery = z.infer<typeof TicketEventsQuerySchema>;

export const TicketEventSchema = z.object({
  id: z.number().int().positive(),
  ticketId: z.number().int().positive(),
  /** FLAGGED_FOR_REVIEW — блокировка приза; TRANSFERRED — только в старых событиях, перенос убран. */
  eventType: z.enum([
    'ACCRUED', 'RECIPIENT_CHANGED', 'REVOKED', 'REACTIVATED', 'TRANSFERRED', 'FLAGGED_FOR_REVIEW', 'PRIZE_AWARDED', 'PRIZE_CANCELLED',
    'MANUAL_GRANTED', 'MANUAL_REVOKED', 'PRIZE_UNBLOCKED', 'PRIZE_RESTORED',
  ]),
  participantId: z.string(),
  originParticipantId: z.string(),
  status: z.enum(['NEW', 'OPENED', 'REVOKED', 'PENDING_REVIEW']),
  reason: z.string().nullable(),
  /** `sync`, `restore` (восстановлено при вводе журнала), `participant` (открытие козочки) или email сотрудника. */
  actor: z.string(),
  manualActionId: z.number().int().positive().nullable(),
  /** Приз события выигрыша, отмены, блокировки, снятия блокировки и возврата приза. */
  prizeId: z.number().int().positive().nullable(),
  createdAt: z.string(),
  conditionType: ConditionTypeSchema,
  sourceRef: z.string(),
});
export type TicketEventDto = z.infer<typeof TicketEventSchema>;

export const TicketEventsResponseSchema = z.object({
  items: z.array(TicketEventSchema),
  hasMore: z.boolean(),
});
export type TicketEventsResponse = z.infer<typeof TicketEventsResponseSchema>;

export const ManualActionSchema = z.object({
  id: z.number().int().positive(),
  actor: z.string(),
  /** GRANT — ручное начисление, REVOKE — ручное списание; TRANSFER и CIC_DEPTH_EXCEEDED — старые записи. */
  actionType: z.enum(['GRANT', 'REVOKE', 'TRANSFER', 'CIC_DEPTH_EXCEEDED']),
  fromParticipantId: z.string().nullable(),
  toParticipantId: z.string().nullable(),
  ticketId: z.number().int().positive().nullable(),
  reason: z.string(),
  requestedCount: z.number().int().nullable(),
  transferredCount: z.number().int().nullable(),
  status: z.enum(['IN_PROGRESS', 'DONE']),
  createdAt: z.string(),
  finishedAt: z.string().nullable(),
});
export type ManualActionDto = z.infer<typeof ManualActionSchema>;

// Профиль участника акции: тип и страна (participant-profile).
export const ParticipantProfileSchema = z.object({
  participantId: z.string(),
  participantType: z.enum(['MANAGER', 'CLIENT']),
  status: z.enum(['PARTICIPATING', 'COUNTRY_NOT_IN_PROMO', 'ANONYMOUS']),
  countryCode: z.string().nullable(),
  countrySource: z.enum(['PARTNER_PREFIX', 'CLIENT_COUNTRY_ID', 'MENTOR_PREFIX', 'DEFAULT_RU']).nullable(),
  updatedAt: z.string(),
});
export type ParticipantProfileDto = z.infer<typeof ParticipantProfileSchema>;

// Вход участника через SSO NL (participant-auth).
export const AuthErrorCodeSchema = z.enum(['STATE_MISMATCH', 'CODE_INVALID', 'SSO_UNAVAILABLE', 'NOT_ALLOWED']);
export type AuthErrorCode = z.infer<typeof AuthErrorCodeSchema>;

export const SsoCallbackQuerySchema = z.object({
  code: z.string().min(1).optional(),
  state: z.string().min(1).optional(),
});

export const AuthMeSchema = z.object({
  participantId: z.string(),
  fullName: z.string(),
  profile: ParticipantProfileSchema.nullable(),
  profileIssue: z.enum(['NOT_FOUND', 'UNAVAILABLE']).nullable(),
  /** Название страны акции по profile.countryCode; null — страна не определена. */
  countryTitle: z.string().nullable(),
  devLogin: z.boolean(),
});
export type AuthMe = z.infer<typeof AuthMeSchema>;

export const DevLoginInputSchema = z.object({
  participantId: z.string().trim().min(1).max(64),
  secret: z.string().min(1).max(256),
});
export type DevLoginInput = z.infer<typeof DevLoginInputSchema>;

/** Какие способы входа доступны в этом окружении — фронт решает, что показывать. */
export const AuthConfigSchema = z.object({
  ssoEnabled: z.boolean(),
  devLoginEnabled: z.boolean(),
});
export type AuthConfig = z.infer<typeof AuthConfigSchema>;

export const OkSchema = z.object({ ok: z.literal(true) });

/** Сохранить язык лендинга вошедшего участника — на нём пойдут сообщения ему (localization). */
export const ParticipantLanguageInputSchema = z.object({
  language: z.enum(LANGUAGE_CODES),
});
export type ParticipantLanguageInput = z.infer<typeof ParticipantLanguageInputSchema>;

/** Состояние лендинга вошедшего участника (add-promo-landing): участие и баланс козочек.
 *  balance/goatsTotal — null, пока участие не подтверждено (козочки есть в леджере, но не показываются). */
export const LandingStateSchema = z.object({
  participationConfirmed: z.boolean(),
  balance: z.number().int().nonnegative().nullable(),
  goatsTotal: z.number().int().nonnegative().nullable(),
});
export type LandingState = z.infer<typeof LandingStateSchema>;

// --- Вход в админку по email + коду на почту (admin-auth), независимо от participant-auth ---

export const AdminRequestCodeInputSchema = z.object({
  email: z.string().email(),
});
export type AdminRequestCodeInput = z.infer<typeof AdminRequestCodeInputSchema>;

export const AdminVerifyCodeInputSchema = z.object({
  email: z.string().email(),
  code: z.string().min(1),
});
export type AdminVerifyCodeInput = z.infer<typeof AdminVerifyCodeInputSchema>;

export const AdminAuthErrorCodeSchema = z.enum(['CODE_INVALID']);
export type AdminAuthErrorCode = z.infer<typeof AdminAuthErrorCodeSchema>;

export const AdminMeSchema = z.object({
  email: z.string(),
});
export type AdminMe = z.infer<typeof AdminMeSchema>;

export const AdminAllowedEmailsResponseSchema = z.object({
  emails: z.array(z.string()),
});
export type AdminAllowedEmailsResponse = z.infer<typeof AdminAllowedEmailsResponseSchema>;

export const AdminAllowedEmailInputSchema = z.object({
  email: z.string().email(),
});
export type AdminAllowedEmailInput = z.infer<typeof AdminAllowedEmailInputSchema>;

export const AdminRenameAllowedEmailInputSchema = z.object({
  email: z.string().email(),
});
export type AdminRenameAllowedEmailInput = z.infer<typeof AdminRenameAllowedEmailInputSchema>;

// --- Сводка по билетам в админке (ticket-dashboard) ---

export const TicketStatusCountsSchema = z.object({
  NEW: z.number().int().nonnegative(),
  OPENED: z.number().int().nonnegative(),
  REVOKED: z.number().int().nonnegative(),
  PENDING_REVIEW: z.number().int().nonnegative(),
  /** Все когда-либо начисленные билеты — сумма по статусам. */
  total: z.number().int().nonnegative(),
});
export type TicketStatusCounts = z.infer<typeof TicketStatusCountsSchema>;

export const TicketSyncRunSchema = z.object({
  startedAt: z.string(),
  finishedAt: z.string(),
  outcome: z.enum(['SUCCESS', 'ERROR']),
  error: z.string().nullable(),
  dryRun: z.boolean(),
  totalMs: z.number().int().nonnegative(),
  targetsCount: z.number().int().nullable(),
  insertedCount: z.number().int().nullable(),
  reactivatedCount: z.number().int().nullable(),
  revokedCount: z.number().int().nullable(),
  flaggedCount: z.number().int().nullable(),
});
export type TicketSyncRunDto = z.infer<typeof TicketSyncRunSchema>;

/** Состояние синка: код, текст выбирает фронт. */
export const TicketSyncHealthSchema = z.enum(['OK', 'ERROR', 'STALE', 'OUT_OF_WINDOW']);
export type TicketSyncHealth = z.infer<typeof TicketSyncHealthSchema>;

export const TicketSyncStatusSchema = z.object({
  health: TicketSyncHealthSchema,
  staleAfterMinutes: z.number().int().positive(),
  /** Время сервера на момент ответа — от него админка считает «N мин назад». */
  checkedAt: z.string(),
  lastRun: TicketSyncRunSchema.nullable(),
  lastSuccessAt: z.string().nullable(),
});
export type TicketSyncStatus = z.infer<typeof TicketSyncStatusSchema>;

// --- Мгновенный розыгрыш (add-instant-draw) ---

const countryCode = z.string().regex(/^[a-z]{2}$/);
const catalogMonth = z.string().regex(/^\d{6}$/);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const PrizeCategorySchema = z.enum(['BUSINESS', 'BONUS', 'TECH', 'PRODUCT', 'PROMO']);
export const PrizePoolSchema = z.enum(['ALL', 'PARTNERS_ONLY']);
export const PrizeTypeSchema = z.enum(['FIXED', 'NON_FIXED']);
export const CostTypeSchema = z.enum(['PAID', 'CONDITIONALLY_FREE']);
/** Тип приза — способ выдачи (add-prize-kind); не путать с prizeType лота (FIXED/NON_FIXED). */
export const PrizeKindSchema = z.enum([
  'BONUS_POINTS', 'PROMO_CODE', 'PHYSICAL', 'MENTOR_DOUBLE_BONUS', 'BUSINESS_MATERIAL', 'BUSINESS_SERVICE', 'COURSE',
]);
export type PrizeKindDto = z.infer<typeof PrizeKindSchema>;
export const BonusCurrencySchema = z.enum(['RUB', 'KZT', 'UZS', 'BYN', 'KGS', 'MDL', 'TJS', 'GEL', 'AZN']);
/** UNIT — Юнит-счёт, ACTIVE — Актив, REWARD — Вознаграждение. */
export const BonusAccountSchema = z.enum(['UNIT', 'ACTIVE', 'REWARD']);

/** Условия промокода. Кода здесь нет — он у выигрыша. Правила применения решает NL. */
export const PromoTermsSchema = z.object({
  discountPercent: z.number().int().nullable(),
  skus: z.array(z.string()),
  itemQty: z.number().int().positive().nullable(),
  validDays: z.number().int().positive().nullable(),
});
export type PromoTermsDto = z.infer<typeof PromoTermsSchema>;

/** Реквизиты бонусных баллов; null в поле — ещё не заполнено. Счёт — не реквизит каталога, см. AdminAward.bonusAccount. */
export const BonusTermsSchema = z.object({
  amount: z.number().positive().nullable(),
  currency: BonusCurrencySchema.nullable(),
});
export type BonusTermsDto = z.infer<typeof BonusTermsSchema>;

/** Приз в ответе участнику: только то, что показывается на карточке поля. */
export const DrawPrizeSchema = z.object({
  prizeId: z.number().int().positive(),
  prizeType: PrizeTypeSchema,
  category: PrizeCategorySchema,
  name: z.string(),
  fullName: z.string(),
  promo: PromoTermsSchema.nullable(),
});
export type DrawPrizeDto = z.infer<typeof DrawPrizeSchema>;

/** Открытие козочки: выигрыш, 8 призов для остальных ячеек поля 3×3 и неоткрытый остаток. */
export const DrawResultSchema = z.object({
  prize: DrawPrizeSchema,
  cells: z.array(DrawPrizeSchema),
  balance: z.number().int().nonnegative(),
  /** Текст канала «попап» на языке лендинга; null — показать стандартное сообщение (add-prize-win-messages). */
  popupMessage: z.string().nullable(),
});
export type DrawResult = z.infer<typeof DrawResultSchema>;

/** Действующий выигрыш участника для «Мои подарки» на лендинге; новые сверху. */
export const LandingAwardSchema = z.object({
  awardId: z.number().int().positive(),
  drawnAt: z.string(),
  prize: DrawPrizeSchema,
  /** Текст закреплённого варианта «Мои призы» на языке лендинга; null — сообщения нет. */
  message: z.string().nullable(),
});
export type LandingAward = z.infer<typeof LandingAwardSchema>;
export const LandingAwardsResponseSchema = z.object({ items: z.array(LandingAwardSchema) });
export type LandingAwardsResponse = z.infer<typeof LandingAwardsResponseSchema>;

/** Коды отказа открытия — текст по коду выбирает фронт. */
export const DrawErrorCodeSchema = z.enum(['NO_TICKETS', 'PARTICIPATION_NOT_CONFIRMED', 'NOT_ELIGIBLE', 'DRAW_CLOSED', 'DRAW_BUSY']);
export type DrawErrorCode = z.infer<typeof DrawErrorCodeSchema>;
export const DrawErrorSchema = z.object({ code: DrawErrorCodeSchema });

// Админка розыгрыша (/api/admin/draw/*).

export const AdminPrizeItemSchema = z.object({
  itemId: z.number().int().positive(),
  category: PrizeCategorySchema,
  kind: PrizeKindSchema.nullable(),
  taxable: z.boolean(),
  nlSku: z.string().nullable(),
  name: z.string(),
  fullName: z.string(),
  promo: PromoTermsSchema.nullable(),
  bonus: BonusTermsSchema.nullable(),
  /** Только у типа BUSINESS_MATERIAL; видно только в админке (add-prize-material-link). */
  materialUrl: z.string().nullable(),
  /** Контакт для связи у типа BUSINESS_SERVICE — markdown, до участника доходит только через тексты (add-service-contact). */
  serviceContact: z.string().nullable(),
  lotCount: z.number().int().nonnegative(),
  updatedAt: z.string(),
});
export type AdminPrizeItem = z.infer<typeof AdminPrizeItemSchema>;

export const PrizeOverridesSchema = z.object({
  name: z.string().optional(),
  fullName: z.string().optional(),
  nlSku: z.string().optional(),
  taxable: z.boolean().optional(),
  promoSkus: z.array(z.string()).optional(),
  discountPercent: z.number().int().optional(),
  itemQty: z.number().int().positive().optional(),
  validDays: z.number().int().positive().optional(),
  bonusAmount: z.number().positive().optional(),
  bonusCurrency: BonusCurrencySchema.optional(),
  materialUrl: z.string().optional(),
  serviceContact: z.string().optional(),
});

export const AdminPrizeLotSchema = z.object({
  prizeId: z.number().int().positive(),
  itemId: z.number().int().positive(),
  country: z.string(),
  month: z.string(),
  pool: PrizePoolSchema,
  prizeType: PrizeTypeSchema,
  limitWeek: z.number().int().nullable(),
  limitMonth: z.number().int().nullable(),
  costType: CostTypeSchema.nullable(),
  unitPriceRub: z.number().nullable(),
  costRub: z.number().nullable(),
  active: z.boolean(),
  category: PrizeCategorySchema,
  kind: PrizeKindSchema.nullable(),
  taxable: z.boolean(),
  name: z.string(),
  fullName: z.string(),
  nlSku: z.string().nullable(),
  promo: PromoTermsSchema.nullable(),
  bonus: BonusTermsSchema.nullable(),
  /** Действующее значение: переопределение лота, иначе значение карточки (add-prize-material-link). */
  materialUrl: z.string().nullable(),
  /** Действующее значение: переопределение лота, иначе значение карточки (add-service-contact). */
  serviceContact: z.string().nullable(),
  overrides: PrizeOverridesSchema,
  updatedAt: z.string(),
});
export type AdminPrizeLot = z.infer<typeof AdminPrizeLotSchema>;

export const AdminPrizeItemsQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  category: PrizeCategorySchema.optional(),
  kind: PrizeKindSchema.optional(),
  physicalWithoutSku: z.enum(['true', 'false']).optional(),
  ...pageQuery,
});
export type AdminPrizeItemsQuery = z.infer<typeof AdminPrizeItemsQuerySchema>;

export const AdminPrizeItemsResponseSchema = z.object({ items: z.array(AdminPrizeItemSchema), hasMore: z.boolean() });

export const AdminPrizeItemDetailSchema = z.object({ item: AdminPrizeItemSchema, lots: z.array(AdminPrizeLotSchema) });
export type AdminPrizeItemDetail = z.infer<typeof AdminPrizeItemDetailSchema>;

const trimmed = z.string().trim();

/** Правка карточки: отсутствующее поле не меняется; null в nlSku — очистить, в promo.itemQty — 255 по умолчанию. */
export const AdminPrizeItemPatchSchema = z.object({
  name: trimmed.min(1).max(300).optional(),
  fullName: trimmed.min(1).max(1000).optional(),
  kind: PrizeKindSchema.optional(),
  taxable: z.boolean().optional(),
  nlSku: trimmed.max(64).nullable().optional(),
  promo: z
    .object({
      discountPercent: z.number().int().min(1).max(100).optional(),
      skus: z.array(trimmed.min(1).max(64)).min(1).max(100).optional(),
      itemQty: z.number().int().positive().nullable().optional(),
      validDays: z.number().int().positive().max(365).optional(),
    })
    .optional(),
  bonus: z
    .object({
      amount: z.number().positive().max(1e12).optional(),
      currency: BonusCurrencySchema.optional(),
    })
    .optional(),
  materialUrl: trimmed.min(1).max(2000).nullable().optional(),
  serviceContact: trimmed.min(1).max(1000).nullable().optional(),
});
export type AdminPrizeItemPatch = z.infer<typeof AdminPrizeItemPatchSchema>;

/** Правка лота: в overrides null — снять переопределение (лот снова берёт значение карточки). */
export const AdminPrizeLotPatchSchema = z.object({
  limitWeek: z.number().int().nonnegative().nullable().optional(),
  limitMonth: z.number().int().nonnegative().nullable().optional(),
  prizeType: PrizeTypeSchema.optional(),
  active: z.boolean().optional(),
  overrides: z
    .object({
      name: trimmed.min(1).max(300).nullable().optional(),
      fullName: trimmed.min(1).max(1000).nullable().optional(),
      nlSku: trimmed.min(1).max(64).nullable().optional(),
      taxable: z.boolean().nullable().optional(),
      promoSkus: z.array(trimmed.min(1).max(64)).min(1).max(100).nullable().optional(),
      discountPercent: z.number().int().min(1).max(100).nullable().optional(),
      itemQty: z.number().int().positive().nullable().optional(),
      validDays: z.number().int().positive().max(365).nullable().optional(),
      bonusAmount: z.number().positive().max(1e12).nullable().optional(),
      bonusCurrency: BonusCurrencySchema.nullable().optional(),
      materialUrl: trimmed.min(1).max(2000).nullable().optional(),
      serviceContact: trimmed.min(1).max(1000).nullable().optional(),
    })
    .optional(),
});
export type AdminPrizeLotPatch = z.infer<typeof AdminPrizeLotPatchSchema>;

// Переводы каталога призов (/api/admin/draw/translations/*) — страница управления переводами.

/** Перевод на один язык (не `ru` — он уже в базовом `name`/`fullName`); пустая строка — нет перевода. */
export const PrizeTranslationSchema = z.object({ name: z.string(), fullName: z.string() });
export type PrizeTranslation = z.infer<typeof PrizeTranslationSchema>;

/** Карта перевода по каждому языку проекта кроме `ru`; отсутствие записи — тоже «нет перевода». */
export const PrizeTranslationsSchema = z.record(z.enum(TRANSLATABLE_LANGUAGE_CODES), PrizeTranslationSchema);
export type PrizeTranslations = z.infer<typeof PrizeTranslationsSchema>;

/** Правка перевода: отсутствующее поле — не менять, пустая строка — удалить перевод на этот язык. */
export const PrizeTranslationPatchSchema = z.object({
  name: z.string().max(300).optional(),
  fullName: z.string().max(1000).optional(),
});
// z.record с enum-ключами требует ВСЕ ключи сразу (как z.object без .partial()) — для правки нужен
// частичный объект, поэтому строим z.object с каждым языком как optional-полем, а не z.record.
export const PrizeTranslationsPatchSchema = z.object(
  Object.fromEntries(TRANSLATABLE_LANGUAGE_CODES.map((locale) => [locale, PrizeTranslationPatchSchema.optional()])),
) as z.ZodObject<Record<(typeof TRANSLATABLE_LANGUAGE_CODES)[number], z.ZodOptional<typeof PrizeTranslationPatchSchema>>>;
export type PrizeTranslationsPatch = z.infer<typeof PrizeTranslationsPatchSchema>;

/** Доля заполненных переводов на языке — раздельно для карточек и переопределений лотов. */
export const AdminTranslationStatusSchema = z.object({
  locale: z.enum(LANGUAGE_CODES),
  items: z.object({ translated: z.number().int().nonnegative(), total: z.number().int().nonnegative() }),
  lotOverrides: z.object({ translated: z.number().int().nonnegative(), total: z.number().int().nonnegative() }),
});
export type AdminTranslationStatus = z.infer<typeof AdminTranslationStatusSchema>;
export const AdminTranslationsStatusResponseSchema = z.object({ languages: z.array(AdminTranslationStatusSchema) });
export type AdminTranslationsStatusResponse = z.infer<typeof AdminTranslationsStatusResponseSchema>;

export const AdminTranslationsImportResultSchema = z.object({ updated: z.number().int().nonnegative() });
export type AdminTranslationsImportResult = z.infer<typeof AdminTranslationsImportResultSchema>;

// Тексты о призах по каналам (add-prize-messages) — хранение и формирование, отдельно от переводов названий.

/** Канал, по которому может быть свой текст о выигрыше. Отправка по каналу — вне этой capability. */
export const MessageChannelSchema = z.enum(['POPUP', 'MY_PRIZES_CARD', 'NL_LK_MESSAGE', 'PUSH', 'SMS']);
export type MessageChannelDto = z.infer<typeof MessageChannelSchema>;
const MESSAGE_CHANNEL_CODES = MessageChannelSchema.options;

/** Вариант текста канала (add-prize-message-variants): `id` стабилен внутри канала, к нему привязаны переводы. */
export const MessageVariantSchema = z.object({ id: z.number().int().positive(), text: z.string() });
export type MessageVariantDto = z.infer<typeof MessageVariantSchema>;
const byChannel = <S extends z.ZodTypeAny>(schema: S) =>
  z.object(Object.fromEntries(MESSAGE_CHANNEL_CODES.map((c) => [c, schema]))) as z.ZodObject<Record<MessageChannelDto, S>>;

/** Варианты каждого канала на `ru`; пустой список — у канала нет текста. */
export const PrizeMessageTextsSchema = byChannel(z.array(MessageVariantSchema));
export type PrizeMessageTexts = z.infer<typeof PrizeMessageTextsSchema>;

/**
 * Правка вариантов: переданный канал — полный новый список вариантов (вариант без `id` — новый, отсутствующий
 * `id` — удалить вместе с переводами, пустой список — у канала нет текста); не переданный канал не меняется.
 */
const MessageVariantInputSchema = z.object({ id: z.number().int().positive().optional(), text: z.string().max(2000) });
export const PrizeMessageTextsPatchSchema = byChannel(z.array(MessageVariantInputSchema).max(50).optional());
export type PrizeMessageTextsPatch = z.infer<typeof PrizeMessageTextsPatchSchema>;

/** Переводы по каждому языку кроме `ru`: канал → переведённые варианты (только те, у которых есть перевод). */
export const PrizeMessageTranslationsSchema = z.record(z.enum(TRANSLATABLE_LANGUAGE_CODES), PrizeMessageTextsSchema);
export type PrizeMessageTranslations = z.infer<typeof PrizeMessageTranslationsSchema>;

/** Правка переводов: перечисленные варианты получают перевод, пустая строка удаляет перевод варианта. */
const MessageVariantTranslationInputSchema = z.object({ id: z.number().int().positive(), text: z.string().max(2000) });
const PrizeMessageTranslationEntryPatchSchema = byChannel(z.array(MessageVariantTranslationInputSchema).max(50).optional());
export const PrizeMessageTranslationsPatchSchema = z.object(
  Object.fromEntries(TRANSLATABLE_LANGUAGE_CODES.map((locale) => [locale, PrizeMessageTranslationEntryPatchSchema.optional()])),
) as z.ZodObject<Record<(typeof TRANSLATABLE_LANGUAGE_CODES)[number], z.ZodOptional<typeof PrizeMessageTranslationEntryPatchSchema>>>;
export type PrizeMessageTranslationsPatch = z.infer<typeof PrizeMessageTranslationsPatchSchema>;

/** Предпросмотр в админке: синтетический контекст выигрыша, чтобы увидеть подстановку без реального приза. */
export const PrizeMessagePreviewInputSchema = z.object({
  locale: z.enum(LANGUAGE_CODES).optional(),
  participantType: z.enum(['MANAGER', 'CLIENT']).optional(),
});
export type PrizeMessagePreviewInput = z.infer<typeof PrizeMessagePreviewInputSchema>;

/** Предпросмотр — все варианты канала с подстановкой (формирование для отправки берёт один случайный). */
export const ComposedMessageVariantsSchema = z.object({ channel: MessageChannelSchema, variants: z.array(z.string()) });
export const PrizeMessagePreviewResponseSchema = z.object({ messages: z.array(ComposedMessageVariantsSchema) });
export type PrizeMessagePreviewResponse = z.infer<typeof PrizeMessagePreviewResponseSchema>;

/** Доля заполненных текстов на каждом языке — раздельно для типов приза и переопределений карточек. */
export const AdminMessageTranslationStatusSchema = z.object({
  locale: z.enum(LANGUAGE_CODES),
  kinds: z.object({ translated: z.number().int().nonnegative(), total: z.number().int().nonnegative() }),
  items: z.object({ translated: z.number().int().nonnegative(), total: z.number().int().nonnegative() }),
});
export const AdminMessageTranslationsStatusResponseSchema = z.object({ languages: z.array(AdminMessageTranslationStatusSchema) });
export type AdminMessageTranslationsStatusResponse = z.infer<typeof AdminMessageTranslationsStatusResponseSchema>;

export const AdminMessageTranslationsImportResultSchema = z.object({ updated: z.number().int().nonnegative() });
export type AdminMessageTranslationsImportResult = z.infer<typeof AdminMessageTranslationsImportResultSchema>;

export const AdminCountryMonthQuerySchema = z.object({ country: countryCode, month: catalogMonth });
export type AdminCountryMonthQuery = z.infer<typeof AdminCountryMonthQuerySchema>;

export const AdminPrizeLotsResponseSchema = z.object({ lots: z.array(AdminPrizeLotSchema) });

export const AdminDrawPlanQuerySchema = z.object({ country: countryCode, month: catalogMonth, week: isoDate.optional() });
export type AdminDrawPlanQuery = z.infer<typeof AdminDrawPlanQuerySchema>;

export const AdminDrawPlanSchema = z.object({
  country: z.string(),
  month: z.string(),
  weekStart: z.string(),
  weeks: z.array(z.string()),
  fixed: z.array(
    z.object({
      prizeId: z.number().int().positive(),
      name: z.string(),
      category: PrizeCategorySchema,
      pool: PrizePoolSchema,
      active: z.boolean(),
      limitWeek: z.number().int().nullable(),
      limitMonth: z.number().int().nullable(),
      issuedWeek: z.number().int().nonnegative(),
      issuedMonth: z.number().int().nonnegative(),
      remaining: z.number().int().nonnegative(),
    }),
  ),
  groups: z.array(
    z.object({
      group: z.number().int(),
      share: z.number(),
      lots: z.number().int().nonnegative(),
      issuedWeek: z.number().int().nonnegative(),
      issuedMonth: z.number().int().nonnegative(),
    }),
  ),
});
export type AdminDrawPlan = z.infer<typeof AdminDrawPlanSchema>;

export const AdminGroupSharesSchema = z.object({
  country: countryCode,
  month: catalogMonth,
  shares: z.record(z.string().regex(/^\d+$/), z.number().min(0).max(1)),
});
export type AdminGroupShares = z.infer<typeof AdminGroupSharesSchema>;

export const AdminDrawCurveSchema = z.object({
  coefficients: z.tuple([z.number(), z.number(), z.number(), z.number()]),
});
export type AdminDrawCurve = z.infer<typeof AdminDrawCurveSchema>;

export const AdminCatalogChangeSchema = z.object({
  id: z.number().int().positive(),
  entity: z.enum(['ITEM', 'PRIZE', 'GROUP_SHARES', 'CURVE']),
  entityKey: z.string(),
  field: z.string(),
  oldValue: z.string().nullable(),
  newValue: z.string().nullable(),
  actor: z.string(),
  createdAt: z.string(),
});
export type AdminCatalogChange = z.infer<typeof AdminCatalogChangeSchema>;

export const AdminCatalogChangesQuerySchema = z.object({ ...pageQuery });
export const AdminCatalogChangesResponseSchema = z.object({ items: z.array(AdminCatalogChangeSchema), hasMore: z.boolean() });

export const AdminAwardSchema = z.object({
  id: z.number().int().positive(),
  ticketId: z.number().int().positive(),
  participantId: z.string(),
  participantType: z.enum(['MANAGER', 'CLIENT']),
  country: z.string(),
  month: z.string(),
  weekStart: z.string(),
  hour: z.number().int().nonnegative(),
  prizeId: z.number().int().positive(),
  prizeType: PrizeTypeSchema,
  prizeName: z.string(),
  /** Категория карточки приза — по ней админка показывает картинку типа; null — лота нет в каталоге. */
  prizeCategory: PrizeCategorySchema.nullable(),
  prizeKind: PrizeKindSchema.nullable(),
  /** Действующие реквизиты лота — правка в каталоге видна и в выданных выигрышах. */
  nlSku: z.string().nullable(),
  promo: PromoTermsSchema.nullable(),
  bonus: BonusTermsSchema.nullable(),
  /** Ссылка на материал готового материала — видна только в админке (add-prize-material-link). */
  materialUrl: z.string().nullable(),
  /** Контакт для связи личной услуги — действующий контакт лота (add-service-contact). */
  serviceContact: z.string().nullable(),
  /** Счёт начисления бонуса — вычисляется по типу участника и стране выигрыша, не из каталога (bonus-account-by-winner). */
  bonusAccount: BonusAccountSchema.nullable(),
  /** Код промокода самого выигрыша — не меняется правками каталога. */
  promoCode: z.string().nullable(),
  status: z.enum(['ACTIVE', 'CANCELLED']),
  repeatAllowed: z.boolean(),
  drawnAt: z.string(),
  cancelledAt: z.string().nullable(),
  cancelledBy: z.string().nullable(),
  cancelReason: z.string().nullable(),
  /** Приз заблокирован: источник больше не даёт билет владельцу; участнику не показывается, не выдаётся. */
  blocked: z.boolean(),
  blockReason: z.string().nullable(),
  blockedAt: z.string().nullable(),
});
export type AdminAward = z.infer<typeof AdminAwardSchema>;

/** Реестр заблокированных призов (instant-draw, «Реестр заблокированных призов»): выигрыш и источник билета. */
export const BlockedAwardSchema = AdminAwardSchema.extend({ conditionType: ConditionTypeSchema, sourceRef: z.string() });
export type BlockedAward = z.infer<typeof BlockedAwardSchema>;

export const AdminAwardsQuerySchema = z.object({
  participantId: z.string().trim().min(1).max(64).optional(),
  prizeId: z.coerce.number().int().positive().optional(),
  country: countryCode.optional(),
  month: catalogMonth.optional(),
  week: isoDate.optional(),
  ...pageQuery,
});
export type AdminAwardsQuery = z.infer<typeof AdminAwardsQuerySchema>;

export const AdminAwardsResponseSchema = z.object({ items: z.array(AdminAwardSchema), hasMore: z.boolean() });

export const AdminAwardCancelInputSchema = z.object({ reason: z.string().trim().min(1).max(1000) });
export const AdminAwardRestoreInputSchema = AdminAwardCancelInputSchema;

export const BlockedAwardsQuerySchema = z.object({ ...pageQuery });
export const BlockedAwardsResponseSchema = z.object({ items: z.array(BlockedAwardSchema), hasMore: z.boolean() });

// --- Участники в админке (participant-admin) ---

const yesNo = z.enum(['yes', 'no']);

export const AdminParticipantsQuerySchema = z.object({
  /** Начало ID участника. */
  q: z.string().trim().min(1).max(64).optional(),
  visited: yesNo.optional(),
  confirmed: yesNo.optional(),
  ...pageQuery,
});
export type AdminParticipantsQuery = z.infer<typeof AdminParticipantsQuerySchema>;

const count = z.number().int().nonnegative();

/** Строка списка и шапка карточки участника: одна форма. Поля null — события не было (не заходил, не подтвердил, профиль не определялся). */
export const AdminParticipantSchema = z.object({
  participantId: z.string(),
  fullName: z.string().nullable(),
  /** Сохранённый профиль, без похода в озеро; свежий — `GET /api/admin/tickets/participants/:id/profile`. */
  profile: ParticipantProfileSchema.nullable(),
  firstVisitAt: z.string().nullable(),
  lastVisitAt: z.string().nullable(),
  confirmedAt: z.string().nullable(),
  /** Козочки, где участник — текущий владелец: всего и по статусам. */
  goats: z.object({ total: count, unopened: count, opened: count, revoked: count, pendingReview: count }),
  activePrizes: count,
});
export type AdminParticipant = z.infer<typeof AdminParticipantSchema>;

export const ParticipationResetSchema = z.object({
  id: z.number().int().positive(),
  participantId: z.string(),
  /** Время подтверждения, которое сбросили. */
  confirmedAt: z.string(),
  resetAt: z.string(),
  actor: z.string(),
  reason: z.string(),
});
export type ParticipationResetDto = z.infer<typeof ParticipationResetSchema>;

/** Карточка: сводка и журнал сбросов подтверждения, новые сверху. */
export const AdminParticipantCardSchema = AdminParticipantSchema.extend({ participationResets: z.array(ParticipationResetSchema) });
export type AdminParticipantCard = z.infer<typeof AdminParticipantCardSchema>;

export const AdminParticipationResetInputSchema = z.object({ reason: z.string().trim().min(1).max(1000) });
export type AdminParticipationResetInput = z.infer<typeof AdminParticipationResetInputSchema>;

export const AdminParticipantsResponseSchema = z.object({ items: z.array(AdminParticipantSchema), hasMore: z.boolean() });
export type AdminParticipantsResponse = z.infer<typeof AdminParticipantsResponseSchema>;

// --- Очередь сообщений в НЛ (nl-message-outbox), формат — openspec/changes/archive/2026-10-31-add-nl-message-outbox/nl-contract.md ---

export const NlMessageTypeSchema = z.enum(['SEND_FEEDBACK_MESSAGE', 'SEND_PUSH', 'SEND_SMS', 'CREATE_PROMO_CODE', 'ACCRUE_BONUS']);
export type NlMessageType = z.infer<typeof NlMessageTypeSchema>;

export const NlOutboxStatusSchema = z.enum(['PENDING', 'SENT', 'DELIVERED', 'FAILED', 'REJECTED', 'CANCELLED']);
export type NlOutboxStatus = z.infer<typeof NlOutboxStatusSchema>;

const nlRecipientId = z.string().trim().min(1).max(64);
const nlText = z.string().refine((t) => t.trim().length > 0, 'текст пустой');
const nlPlainText = nlText.refine((t) => !hasMarkdownLink(t), 'в пуше и смс нет markdown-ссылок — адрес пишется просто текстом');
const nlDateTime = z.string().datetime();

export const SendFeedbackMessagePayloadSchema = z.object({ recipientId: nlRecipientId, text: nlText });
export const SendPushPayloadSchema = z.object({ recipientId: nlRecipientId, text: nlPlainText });
export const SendSmsPayloadSchema = z.object({ recipientId: nlRecipientId, text: nlPlainText });
export const CreatePromoCodePayloadSchema = z
  .object({
    recipientId: nlRecipientId,
    code: z.string().regex(/^NY27[A-Z]{5}$/, 'код — NY27 и 5 латинских заглавных букв'),
    discountPercent: z.number().int().min(1).max(100),
    skus: z.array(z.string().trim().min(1)).min(1).max(100),
    currency: BonusCurrencySchema,
    maxItems: z.number().int().positive().nullable(),
    validFrom: nlDateTime,
    validUntil: nlDateTime,
  })
  .refine((p) => p.validUntil > p.validFrom, { message: 'validUntil должен быть позже validFrom', path: ['validUntil'] });
export const AccrueBonusPayloadSchema = z.object({
  recipientId: nlRecipientId,
  account: BonusAccountSchema,
  amount: z.string().regex(/^\d+(\.\d{1,2})?$/, 'сумма строкой с точкой, до 2 знаков после неё').refine((a) => Number(a) > 0, 'сумма больше 0'),
  currency: BonusCurrencySchema,
});

/** Сообщение, которое бизнес-код ставит в очередь: тип и поля по типу. */
export const NlOutboxMessageInputSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('SEND_FEEDBACK_MESSAGE'), payload: SendFeedbackMessagePayloadSchema }),
  z.object({ type: z.literal('SEND_PUSH'), payload: SendPushPayloadSchema }),
  z.object({ type: z.literal('SEND_SMS'), payload: SendSmsPayloadSchema }),
  z.object({ type: z.literal('CREATE_PROMO_CODE'), payload: CreatePromoCodePayloadSchema }),
  z.object({ type: z.literal('ACCRUE_BONUS'), payload: AccrueBonusPayloadSchema }),
]);
export type NlOutboxMessageInput = z.infer<typeof NlOutboxMessageInputSchema>;

const NL_BATCH_MAX = 500;
const uniqueMessageIds = (items: { messageId: string }[]) => new Set(items.map((i) => i.messageId)).size === items.length;

/** Пачка сообщений в НЛ (раздел 3.1 контракта). */
export const NlBatchRequestSchema = z.object({
  messages: z
    .array(
      z.object({
        messageId: z.string().min(1).max(64),
        type: NlMessageTypeSchema,
        createdAt: z.string(),
        payload: z.record(z.string(), z.unknown()),
      }),
    )
    .min(1)
    .max(NL_BATCH_MAX),
});
export type NlBatchRequest = z.infer<typeof NlBatchRequestSchema>;

/** Ответ НЛ на пачку (раздел 3.3 контракта). Лишние поля НЛ не мешают разбору. */
export const NlBatchResponseSchema = z.object({
  results: z.array(
    z.discriminatedUnion('status', [
      z.object({ messageId: z.string(), status: z.literal('QUEUED') }),
      z.object({ messageId: z.string(), status: z.literal('REJECTED'), errorCode: z.string().min(1), errorMessage: z.string().optional() }),
    ]),
  ),
});
export type NlBatchResponse = z.infer<typeof NlBatchResponseSchema>;

/** Колбэк НЛ с результатами обработки (раздел 5.1 контракта). */
export const NlCallbackResultSchema = z
  .object({
    messageId: z.string().min(1).max(64),
    status: z.enum(['SUCCESS', 'ERROR']),
    processedAt: z.string(),
    externalId: z.string().optional(),
    errorCode: z.string().min(1).optional(),
    errorMessage: z.string().max(1000).optional(),
  })
  .refine((r) => r.status === 'SUCCESS' || r.errorCode !== undefined, { message: 'errorCode обязателен при ERROR', path: ['errorCode'] });
export type NlCallbackResult = z.infer<typeof NlCallbackResultSchema>;

export const NlCallbackRequestSchema = z.object({
  results: z.array(NlCallbackResultSchema).min(1).max(NL_BATCH_MAX).refine(uniqueMessageIds, 'messageId в колбэке повторяется'),
});
export const NlCallbackResponseSchema = z.object({ ok: z.literal(true), unknown: z.array(z.string()) });

/** Сообщение очереди в админке. */
export const AdminNlOutboxMessageSchema = z.object({
  id: z.number().int().positive(),
  type: NlMessageTypeSchema,
  payload: z.record(z.string(), z.unknown()),
  status: NlOutboxStatusSchema,
  attempts: z.number().int().nonnegative(),
  nextAttemptAt: z.string(),
  lastAttemptAt: z.string().nullable(),
  lastError: z.string().nullable(),
  callbackReceivedAt: z.string().nullable(),
  /** Тело результата из колбэка НЛ как есть (JSON-строка). */
  callbackResult: z.string().nullable(),
  /** Исходное сообщение, если это копия ручного повтора. */
  retriedFromId: z.number().int().positive().nullable(),
  /** Копия ручного повтора этого сообщения, если создана. */
  retryCopyId: z.number().int().positive().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AdminNlOutboxMessage = z.infer<typeof AdminNlOutboxMessageSchema>;

export const AdminNlOutboxQuerySchema = z.object({
  type: NlMessageTypeSchema.optional(),
  status: NlOutboxStatusSchema.optional(),
  ...pageQuery,
});
export type AdminNlOutboxQuery = z.infer<typeof AdminNlOutboxQuerySchema>;

export const AdminNlOutboxResponseSchema = z.object({ items: z.array(AdminNlOutboxMessageSchema), hasMore: z.boolean() });
export type AdminNlOutboxResponse = z.infer<typeof AdminNlOutboxResponseSchema>;
